import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { BaseModal } from "../../components/Modal/BaseModal";
import { EditableGrid } from "../../components/DataTable/EditableGrid";
import { Add } from "@mui/icons-material";
import moment from "moment";
import { useAppDispatch } from "../../store/hooks";
import { v4 as uuidv4 } from "uuid";
import { EditDescriptor } from "@progress/kendo-react-data-tools";
import { ActionCell } from "../../components/DataTable/customcell/ActionCell";
import { saveContractPriceData } from "../../slices/contractprice.slice";
import { PriceCell } from "./PriceCell";
import { GridColumnProps } from "@progress/kendo-react-grid";
import { validateContractPricePeriodsGridData } from "../Modals/action-bill-modal/util";

interface Props {
  open: boolean;
  handleClose: () => void;
  editItem: any;
}

const newRow = {
  buyerName: "",
  price: 0,
  bushels: 0,
  contractDate: new Date(),
  notes: "",
};

export const EditContractPriceModal = ({
  handleClose,
  open,
  editItem,
}: Props) => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [edit, setEdit] = useState<EditDescriptor>({});
  const [weightedAveragePrice, setWeightedAveragePrice] = useState<number>(0);
  const finalPriceRef = useRef<number>(0);
  const [saving, setSaving] = useState(false);
  const [isFinalPriceInvalid, setIsFinalPriceInvalid] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (editItem) {
      const { periods } = editItem;
      setTableData(
        periods.map((item: any) => {
          return {
            ...item,
            id: uuidv4(),
            contractDate: moment(item.contractDate, "MM/DD/YYYY").toDate(),
          };
        })
      );
      finalPriceRef.current = Number(editItem.finalPrice) || 0;
    }
  }, [editItem]);

  // Calculate weighted average price whenever tableData changes
  useEffect(() => {
    if (tableData.length > 0) {
      const totalValue = tableData.reduce(
        (sum, item) => sum + (item.price || 0) * (item.bushels || 0),
        0
      );
      const totalBushels = tableData.reduce(
        (sum, item) => sum + (item.bushels || 0),
        0
      );
      const calculatedWeightedAvg =
        totalBushels > 0 ? totalValue / totalBushels : 0;
      setWeightedAveragePrice(calculatedWeightedAvg);
    } else {
      setWeightedAveragePrice(0);
    }
  }, [tableData]);

  const addNewPeriod = () => {
    setTableData((prev) => [...prev, { ...newRow, id: uuidv4() }]);
  };

  const onItemChange = (e: any) => {
    const { field, value, dataItem } = e;
    const updatedData = tableData.map((item: any) => {
      if (item.id === dataItem.id) {
        const updatedRow = {
          ...item,
        };
        updatedRow[field] = value;
        return updatedRow;
      }
      return item;
    });
    setTableData(updatedData);
  };

  useEffect(() => {
    setErrorFields(validateContractPricePeriodsGridData(tableData));
    setEdit(
      tableData.reduce((acc, item) => {
        acc[item.id] = true;
        return acc;
      }, {})
    );
  }, [tableData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const requestData = {
        id: editItem.id,
        cropId: editItem.cropId,
        weightedAveragePrice: weightedAveragePrice,
        finalPrice: finalPriceRef.current || 0,
        year: editItem.year,
        periods: tableData.map((item: any) => ({
          buyerName: item.buyerName,
          price: item.price,
          bushels: item.bushels,
          contractDate: moment(item.contractDate).format("MM/DD/YYYY"),
          notes: item.notes,
        })),
      };

      await dispatch(saveContractPriceData(requestData));
      handleClose();
    } catch (e) {
      // Optionally show error
    } finally {
      setSaving(false);
    }
  };

  const deletePricePeriod = (dataItem: any) => {
    setTableData((prevTableData) =>
      prevTableData.filter((item: any) => item.id !== dataItem.id)
    );
  };

  const handleFinalPriceChange = (event: any) => {
    finalPriceRef.current = Number(event.target.value);
  };

  const columns: GridColumnProps[] = useMemo(
    () => [
      {
        field: "buyerName",
        title: "Buyer Name",
        width: 180,
      },
      {
        field: "price",
        title: "Price",
        width: 140,
        cells: {
          data: (props) => <PriceCell {...props} endAdornment="/bu" />,
        },
      },
      {
        field: "bushels",
        title: "Quantity",
        width: 140,
        cells: {
          data: (props) => (
            <PriceCell {...props} startAdornment="" endAdornment="bu" />
          ),
        },
      },
      {
        field: "contractDate",
        title: "Contract Date",
        editor: "date",
        format: "{0:d}",
        width: 150,
      },
      {
        field: "notes",
        title: "Notes",
        width: 200,
      },
      {
        title: "",
        width: 100,
        cells: {
          data: (props) => (
            <ActionCell {...props} onDelete={deletePricePeriod} />
          ),
        },
      },
    ],
    []
  );

  const handleFinalPriceBlur = () => {
    setIsFinalPriceInvalid(
      !finalPriceRef.current || Number(finalPriceRef.current) === 0
    );
  };
  return (
    <BaseModal open={open} handleClose={handleClose}>
      <Box display={"flex"} flexDirection={"column"} gap={5}>
        <Typography variant="h6">Edit Contract Price</Typography>
        <Box display={"flex"} flexWrap={"wrap"} gap={2}>
          <TextField
            label="Crop Name"
            variant="outlined"
            defaultValue={editItem?.cropName ?? ""}
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            label="Year"
            variant="outlined"
            defaultValue={editItem?.year ?? ""}
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            label="Weighted Avg Price"
            variant="outlined"
            value={`$${weightedAveragePrice.toFixed(2)}/bu`}
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            label="Final Price"
            variant="outlined"
            type="number"
            defaultValue={editItem?.finalPrice || 0}
            onChange={handleFinalPriceChange}
            onBlur={handleFinalPriceBlur}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
              endAdornment: <Typography sx={{ ml: 1 }}>/bu</Typography>,
            }}
            inputProps={{
              step: 0.01,
              min: 0,
            }}
          />
        </Box>
        <Box>
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            mb={1}
            alignItems={"center"}
          >
            <Typography>Contracts</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={addNewPeriod}
              disabled={saving}
            >
              Add Contract
            </Button>
          </Box>

          <Box
            sx={{
              maxHeight: "200px",
              overflow: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "4px",
                "&:hover": {
                  background: "#a8a8a8",
                },
              },
            }}
          >
            <EditableGrid
              dataItemKey="id"
              onItemChange={onItemChange}
              edit={edit}
              columns={columns}
              data={tableData}
            />
          </Box>
        </Box>
        <Box display={"flex"} justifyContent={"flex-end"} gap={2}>
          <Button
            variant="contained"
            color="primary"
            disabled={
              saving ||
              isFinalPriceInvalid ||
              !(tableData.length && !errorFields.length)
            }
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </BaseModal>
  );
};